/* eslint-disable */
import { authHeader } from './auth-header';
import router from './../router/index';
import Config from '@/config';
import ApiQuery from './apiQuery';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Liste paginée retournée par le backend */
export interface ApiResponseList<T = any> {
    entries: T[];
    total: number;
}

/** Réponse objet retournée par le backend (v1 : champs bruts) */
export interface ApiResponse {
    statusCode?: string;
    message?: string;
    [key: string]: any;
}

/** Erreur retournée par le backend */
export interface ApiError {
    statusCode: number;
    message: string;
    details?: any;
}

// ─── Service ──────────────────────────────────────────────────────────────────

class ApiServices {
    // ── Helpers privés ──────────────────────────────────────────────────────

    private buildUrl(objectName: string, suffix = ''): string {
        return `${Config.api.backendUrl}/api/v1/${objectName}${suffix}`;
    }

    private buildHeaders(isFormData = false): Headers {
        const headers: Record<string, string> = {
            'x-api-key': Config.api.BackendApiKey,
            authorization: authHeader(),
        };
        if (!isFormData) {
            headers['Content-Type'] = 'application/json';
        }
        return new Headers(headers);
    }

    /**
     * Gestion centralisée des erreurs HTTP.
     * - 401 / 403 → redirection /login
     * - 400       → redirection /error-400 (sauf si redirectOnError = false)
     * Rejette toujours avec un ApiError typé.
     * @param response réponse HTTP en erreur
     * @param redirectOnError false pour laisser l'appelant gérer une erreur 400 (ex. afficher un message)
     */
    private async handleHttpError(
        response: Response,
        redirectOnError = true,
    ): Promise<never> {
        if (response.status === 401 || response.status === 403) {
            router.push('/login');
        } else if (response.status === 400 && redirectOnError) {
            router.push('/error-400');
        }
        let details: any = {};
        try {
            details = await response.json();
        } catch {}

        return Promise.reject<never>({
            statusCode: response.status,
            message:
                details?.description ?? details?.message ?? response.statusText,
            details,
        } as ApiError);
    }

    /**
     * Résout une réponse HTTP :
     * - 204 / 404 → defaultData
     * - 2xx       → JSON parsé
     * - autres    → handleHttpError
     * @param redirectOnError false pour ne pas rediriger vers /error-400 sur une erreur 400
     */
    private async resolveResponse<T>(
        response: Response,
        defaultData: T,
        redirectOnError = true,
    ): Promise<T> {
        if (response.ok) {
            if (response.status === 204) return defaultData;

            const json = await response.json();

            // Détection enveloppe v2 : { statusCode: string, message: string, data: ... }
            // → on retourne data pour avoir le même résultat qu'en v1
            if (
                json !== null &&
                typeof json === 'object' &&
                typeof json.statusCode === 'string' &&
                'data' in json
            ) {
                return json.data as T;
            }

            return json as T;
        }

        if (response.status === 404) return defaultData;

        return this.handleHttpError(response, redirectOnError);
    }

    // ── Méthodes publiques ───────────────────────────────────────────────────

    async getAll(
        objectName: string,
        filters: any,
        fields: Array<string>,
        pagination: any,
    ): Promise<ApiResponseList> {
        let url = this.buildUrl(objectName);
        url = ApiQuery.parseQueryParam(filters, url);
        if (pagination !== null)
            url = ApiQuery.parsePagination(pagination, url);
        url = ApiQuery.parseFields(fields, url);

        const response = await fetch(url, {
            method: 'GET',
            redirect: 'follow',
            headers: this.buildHeaders(),
        });
        return this.resolveResponse<ApiResponseList>(response, {
            entries: [],
            total: 0,
        });
    }

    async get(objectName: string, filters: any): Promise<ApiResponse> {
        let url = this.buildUrl(objectName);
        url = ApiQuery.parseQueryParam(filters, url);

        const response = await fetch(url, {
            method: 'GET',
            redirect: 'follow',
            headers: this.buildHeaders(),
        });
        return this.resolveResponse<ApiResponse>(response, {} as ApiResponse);
    }

    async getById(objectName: string, id: string): Promise<ApiResponse> {
        const response = await fetch(this.buildUrl(objectName, `/${id}`), {
            method: 'GET',
            redirect: 'follow',
            headers: this.buildHeaders(),
        });
        return this.resolveResponse<ApiResponse>(response, {} as ApiResponse);
    }

    async create(objectName: string, requestBody: any): Promise<ApiResponse> {
        const response = await fetch(this.buildUrl(objectName), {
            method: 'POST',
            redirect: 'follow',
            headers: this.buildHeaders(),
            body: JSON.stringify(requestBody),
        });
        return this.resolveResponse<ApiResponse>(response, {} as ApiResponse);
    }

    async update(
        objectName: string,
        requestBody: any,
        id: string,
    ): Promise<ApiResponse> {
        const response = await fetch(this.buildUrl(objectName, `/${id}`), {
            method: 'PUT',
            redirect: 'follow',
            headers: this.buildHeaders(),
            body: JSON.stringify(requestBody),
        });
        return this.resolveResponse<ApiResponse>(response, {} as ApiResponse);
    }

    async patch(
        objectName: string,
        requestBody: any,
        suffix = '',
    ): Promise<ApiResponse> {
        const response = await fetch(this.buildUrl(objectName, suffix), {
            method: 'PATCH',
            redirect: 'follow',
            headers: this.buildHeaders(),
            body: JSON.stringify(requestBody),
        });
        return this.resolveResponse<ApiResponse>(response, {} as ApiResponse);
    }

    async count(objectName: string, filters: object): Promise<ApiResponse> {
        let url = this.buildUrl(objectName, '/count');
        url = ApiQuery.parseQueryParam(filters, url);

        const response = await fetch(url, {
            method: 'GET',
            redirect: 'follow',
            headers: this.buildHeaders(),
        });
        return this.resolveResponse<ApiResponse>(response, {} as ApiResponse);
    }

    /**
     * Supprime un objet par son id.
     * @param objectName ressource (ex. 'GCS')
     * @param id identifiant de l'objet
     * @param redirectOnError false pour gérer soi-même une erreur 400 au lieu de rediriger vers /error-400
     */
    async delete(
        objectName: string,
        id: string,
        redirectOnError = true,
    ): Promise<ApiResponse> {
        const response = await fetch(this.buildUrl(objectName, `/${id}`), {
            method: 'DELETE',
            redirect: 'follow',
            headers: this.buildHeaders(),
        });
        return this.resolveResponse<ApiResponse>(
            response,
            {} as ApiResponse,
            redirectOnError,
        );
    }

    async setStatus(
        objectName: string,
        status: string,
        id: string,
        filter: any,
    ): Promise<ApiResponse> {
        let url = this.buildUrl(objectName, `/setStatus/${id}`);
        if (filter !== null) url = url + filter;

        const response = await fetch(url, {
            method: 'PUT',
            redirect: 'follow',
            headers: this.buildHeaders(),
            body: JSON.stringify({ status }),
        });
        return this.resolveResponse<ApiResponse>(response, {} as ApiResponse);
    }

    async upload(objectName: string, file: any): Promise<ApiResponse> {
        const formData = new FormData();
        for (const name in file) {
            formData.append(name, file[name]);
        }

        const response = await fetch(this.buildUrl(objectName), {
            method: 'POST',
            redirect: 'follow',
            headers: this.buildHeaders(true), // pas de Content-Type, laissé au navigateur pour multipart
            body: formData,
        });
        return this.resolveResponse<ApiResponse>(response, {} as ApiResponse);
    }

    /**
     * Upload direct d'un fichier vers GCS via une URL signée (sans token API).
     * Les en-têtes doivent correspondre exactement à ceux signés par le backend (GCS/upload-url).
     * @param uploadUrl URL signée retournée par GCS/upload-url
     * @param file fichier à envoyer
     * @param contentType type de contenu signé dans l'URL
     * @param maxSize taille max signée dans l'URL (x-goog-content-length-range)
     * @param onProgress callback optionnel recevant le pourcentage d'avancement
     */
    uploadToSignedUrl(
        uploadUrl: string,
        file: File,
        contentType: string,
        maxSize: number,
        onProgress?: (percent: number) => void,
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', uploadUrl);
            xhr.setRequestHeader('Content-Type', contentType);
            xhr.setRequestHeader('x-goog-content-length-range', `0,${maxSize}`);
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable && onProgress) {
                    onProgress(Math.round((event.loaded / event.total) * 100));
                }
            };
            xhr.onload = () =>
                xhr.status >= 200 && xhr.status < 300
                    ? resolve()
                    : reject(new Error(`Échec de l'envoi vers GCS (${xhr.status})`));
            xhr.onerror = () =>
                reject(new Error("Échec de l'envoi vers GCS (réseau ou CORS)"));
            xhr.send(file);
        });
    }

    async download(
        objectName: string,
        filters: any,
        fileName: string,
    ): Promise<boolean> {
        let url = this.buildUrl(objectName);
        url = ApiQuery.parseQueryParam(filters, url);

        const response = await fetch(url, {
            method: 'GET',
            redirect: 'follow',
            headers: new Headers({
                'Content-Type': 'text/html',
                'x-api-key': Config.api.BackendApiKey,
                authorization: authHeader(),
            }),
        });

        if (!response.ok) return this.handleHttpError(response);

        const blob = await response.blob();
        const fileURL = window.URL.createObjectURL(new Blob([blob]));
        const a = document.createElement('a');
        a.href = fileURL;
        a.setAttribute('download', fileName);
        document.body.appendChild(a);
        a.click();
        return true;
    }

    async generatePdf(
        url: string,
        id: string,
        fileName = 'file',
    ): Promise<void> {
        const response = await fetch(this.buildUrl(url, `/${id}`), {
            method: 'GET',
            redirect: 'follow',
            headers: new Headers({
                'Content-Type': 'text/html',
                'x-api-key': Config.api.BackendApiKey,
                authorization: authHeader(),
            }),
        });

        if (!response.ok) return this.handleHttpError(response);

        const buffer = await response.arrayBuffer();
        const file = new Blob([buffer], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = `${fileName}${id}.pdf`;
        link.click();
    }

    async getBetweenDates(
        objectName: string,
        filters: any,
        startDate: string,
        endDate: string,
    ): Promise<ApiResponse> {
        let url = this.buildUrl(objectName);
        if (startDate != null && endDate != null) {
            url = `${url}?startDate=${startDate}&endDate=${endDate}`;
        }
        url = ApiQuery.parseQueryParam(filters, url);

        const response = await fetch(url, {
            method: 'GET',
            redirect: 'follow',
            headers: this.buildHeaders(),
        });
        return this.resolveResponse<ApiResponse>(response, {} as ApiResponse);
    }
}

export default new ApiServices();
